import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { TranslatePipe } from '@ngx-translate/core';
import { SolPreferencesService } from '../../../../../services/preferences/sol-preferences.service';
import { CostReviewerDetails } from '../../../../../models/dtos/Classes/CostReviewerDetails';
import { ReviewerTypeItem } from '../../../../../models/dtos';
import { CostReviewStatus } from '../../../../../models/dtos/Enums/CostReviewStatus';
import { MatDialog } from '@angular/material/dialog';
import { CostReviewAddModifyReviewerComponent } from '../../../../dialogs/common/cost-review-add-modify-reviewer/cost-review-add-modify-reviewer.component';
import { Subject, takeUntil } from 'rxjs';
import { ICostReviewRequestDialogData } from '../../../../../models/common/ICostReviewRequestDialogData';
import { CommonMetaDataService } from '../../../../../services/common-meta-data.service';

@Component({
  selector: 'app-sol-preferences-cost-reviewers',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    TranslatePipe
  ],
  templateUrl: './sol-preferences-cost-reviewers.component.html',
  styleUrls: ['./sol-preferences-cost-reviewers.component.scss']
})
export class SolPreferencesCostReviewersComponent implements OnInit, OnDestroy {

  private readonly compareByEmail = (a: CostReviewerDetails, b: CostReviewerDetails): boolean => a.reviewerEmail === b.reviewerEmail;
  private readonly destroy$ = new Subject<void>();
  private costReviewRolesTypes: ReviewerTypeItem[] = [];

  // --- Top table: Reviewer(s) ---
  reviewersColumns: string[] = ['role', 'reviewer', 'email'];
  reviewersDataSource = new MatTableDataSource<CostReviewerDetails>();
  reviewersSelection = new SelectionModel<CostReviewerDetails>(false, [], true, this.compareByEmail);

  // --- Bottom table: Available Reviewers ---
  availableColumns: string[] = ['role', 'reviewer', 'email', 'delete'];
  availableDataSource = new MatTableDataSource<CostReviewerDetails>();
  availableSelection = new SelectionModel<CostReviewerDetails>(false, [], true, this.compareByEmail);

  private reviewers: CostReviewerDetails[] = [];
  private availableReviewers: CostReviewerDetails[] = [];
  private readonly maxReviewers = 5;

  constructor(private readonly preferencesService: SolPreferencesService,
    private readonly dialog: MatDialog,
    private readonly commonMetaDataService: CommonMetaDataService,
  ) { }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    const userPreferences = this.preferencesService.getUserPreferences();
    const costReviewerPrefs = userPreferences.costReviewerPreferences;

    this.reviewers = costReviewerPrefs?.crReviewers ?? [];
    this.availableReviewers = costReviewerPrefs?.crAvailableReviewers ?? [];

    this.costReviewRolesTypes = this.commonMetaDataService.getMetaData().costReviewerTypes;
    this.resolveRoleNames(this.reviewers);
    this.resolveRoleNames(this.availableReviewers);

    this.reviewersDataSource.data = this.reviewers;
    this.availableDataSource.data = this.availableReviewers;
  }

  private resolveRoleNames(details: CostReviewerDetails[]): void {
    details.forEach(r => {
      r.reviewerType = this.costReviewRolesTypes?.find(role => role.id === r.reviewerTypeID)?.description ?? '';
    });
  }

  // ---- Row click handlers ----

  onReviewerRowClick(row: CostReviewerDetails): void {
    this.reviewersSelection.toggle(row);
    this.availableSelection.clear();
  }

  onAvailableRowClick(row: CostReviewerDetails): void {
    this.availableSelection.toggle(row);
    this.reviewersSelection.clear();
  }

  // ---- Button states ----

  get isAddEnabled(): boolean {
    return this.reviewersDataSource.data.length < this.maxReviewers
      && !this.availableSelection.isEmpty();
  }

  get isRemoveEnabled(): boolean {
    return !this.reviewersSelection.isEmpty();
  }

  // ---- Actions ----

  addToReviewers(): void {
    if (!this.isAddEnabled) {
      return;
    }
    
    const selected = this.availableSelection.selected[0];

    this.reviewersDataSource.data = [...this.reviewersDataSource.data,selected];
    this.availableDataSource.data = this.availableDataSource.data.filter(reviewer => reviewer !== selected);
    this.availableSelection.clear();
    this.syncToService();
  }

  removeFromReviewers(): void {
    if (this.reviewersSelection.isEmpty()) {
      return;
    }

    const selected = this.reviewersSelection.selected[0];

    this.availableDataSource.data = [...this.availableDataSource.data, selected];
    this.reviewersDataSource.data = this.reviewersDataSource.data.filter(r => r !== selected);
    this.reviewersSelection.clear();
    this.syncToService();
  }

  deleteAvailableReviewer(row: CostReviewerDetails): void {
    this.availableDataSource.data = this.availableDataSource.data.filter(r => r !== row);

    if (this.availableSelection.isSelected(row)) {
      this.availableSelection.clear();
    }
    this.syncToService();
  }

  addReviewer(): void {
    const dialogRef= this.dialog.open(CostReviewAddModifyReviewerComponent,{
      width: '600px',
      height: '280px',
      disableClose: true,
      data: {
        isCostReviewRequest: false,
        dialogData: {
          title: 'ADD_REVIEWER',
          isModifyMode: false,
          existingEmails: []
        }
      }
    })
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((data: ICostReviewRequestDialogData) => {
      if (data) {
        const newRow: CostReviewerDetails = {
          reviewerTypeID: data.reviewerTypeId,
          reviewerName: data.reviewer,
          reviewerEmail: data.email,
          reviewUserId: data.userId,
          requiredBy: data.requiredBy ? data.requiredBy : null,
          closeDate: null,
          reviewerStatus: CostReviewStatus.None,
          reviewerComments: '',
          modifiedBy: data.modifiedBy,
          originalReviewerId: data.originalReviewerId,
          reviewerType: this.costReviewRolesTypes?.find(role => role.id === data.reviewerTypeId)?.description ?? '',
          originalReviewerName: '',
          isOriginalReviewerVisible: false,
          isPolicyMandatedApprover: false
        };
        this.availableDataSource.data = [...this.availableDataSource.data, newRow];
        this.syncToService();
      }
    })
  }

  // Track-by helpers
  trackByEmail(_index: number, row: CostReviewerDetails): string {
    return row.reviewerEmail;
  }

  /** Push current table state back into the shared service. */
  private syncToService(): void {
    this.preferencesService.silentSetPreferences(prefs => {
      prefs.costReviewerPreferences = {
        ...(prefs.costReviewerPreferences ?? {
          crReviewers: [],
          crAvailableReviewers: [],
          crAvailableCCUsers: [],
          crEmailCCUsers: []
        }),
        crReviewers: this.reviewersDataSource.data,
        crAvailableReviewers: this.availableDataSource.data,
        crAvailableCCUsers: prefs.costReviewerPreferences?.crAvailableCCUsers,
        crEmailCCUsers: prefs.costReviewerPreferences?.crEmailCCUsers
      };
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
